import { Search } from 'lucide-react';
import { StylishNewSearchBar } from 'components/DesignSystems/New/StylishNewSearchBar';
import StylishNewSelect from 'components/DesignSystems/New/StylishNewSelect';
import { useAllIncidents } from 'components/IAP/hooks/incidentHooks';
import { useAors } from 'hooks/useAors';
import React, { useMemo } from 'react';
import { Col, Row } from 'react-bootstrap';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useHubChromeEnabled } from 'utils/featureFlags';
import { useAorText } from 'utils/aorTerminology';

interface NotificationsFilterBarProps {
  searchText: string;
  onSearchChange: (value: string) => void;
  selectedAorId: string | null;
  onAorChange: (aorId: string | null) => void;
  selectedIncidentId: string | null;
  onIncidentChange: (incidentId: string | null) => void;
  lockToIncidentId?: string | null;
}

const ALL_VALUE = '__all__';

const NotificationsFilterBar: React.FC<NotificationsFilterBarProps> = ({
  searchText,
  onSearchChange,
  selectedAorId,
  onAorChange,
  selectedIncidentId,
  onIncidentChange,
  lockToIncidentId,
}) => {
  const hubChrome = useHubChromeEnabled();
  const aorText = useAorText();
  const { data: aors = [] } = useAors();
  const { data: incidents = [] } = useAllIncidents();
  const incidentLocked = Boolean(lockToIncidentId);

  const aorOptions = useMemo(
    () =>
      aors.map((aor) => ({
        value: aor.id,
        label: aor.name,
      })),
    [aors]
  );

  const incidentOptions = useMemo(() => {
    const options = incidents.map((incident) => ({
      value: incident.id,
      label: incident.name,
    }));
    if (!lockToIncidentId) return options;
    return options.filter((opt) => opt.value === lockToIncidentId);
  }, [incidents, lockToIncidentId]);

  if (hubChrome) {
    return (
      <div className="tw:space-y-2 tw:pb-2">
        <div className="tw:relative">
          <Search className="tw:pointer-events-none tw:absolute tw:left-2 tw:top-1/2 tw:h-3.5 tw:w-3.5 tw:-translate-y-1/2 tw:text-muted-foreground" />
          <Input
            value={searchText}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Search notifications…"
            className="tw:h-8 tw:pl-8 tw:text-xs"
            aria-label="Search notifications"
          />
        </div>
        <div className="tw:grid tw:gap-2 tw:sm:grid-cols-2">
          <div className="tw:flex tw:min-w-0 tw:items-center tw:gap-2">
            <span className="tw:shrink-0 tw:text-xs tw:text-muted-foreground">
              Incident
            </span>
            <Select
              value={selectedIncidentId ?? ALL_VALUE}
              disabled={incidentLocked}
              onValueChange={(value) => {
                if (incidentLocked) return;
                onIncidentChange(
                  !value || value === ALL_VALUE ? null : String(value)
                );
              }}
            >
              <SelectTrigger
                size="sm"
                disabled={incidentLocked}
                className="tw:min-w-0 tw:flex-1 tw:bg-background tw:text-foreground"
              >
                <SelectValue placeholder="All incidents">
                  {selectedIncidentId
                    ? (incidentOptions.find(
                        (opt) => opt.value === selectedIncidentId
                      )?.label ?? 'All incidents')
                    : 'All incidents'}
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="dark">
                {incidentLocked ? null : (
                  <SelectItem value={ALL_VALUE}>All incidents</SelectItem>
                )}
                {incidentOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="tw:flex tw:min-w-0 tw:items-center tw:gap-2">
            <span className="tw:shrink-0 tw:text-xs tw:text-muted-foreground">
              {aorText('AOR')}
            </span>
            <Select
              value={selectedAorId ?? ALL_VALUE}
              onValueChange={(value) =>
                onAorChange(
                  !value || value === ALL_VALUE ? null : String(value)
                )
              }
            >
              <SelectTrigger
                size="sm"
                className="tw:min-w-0 tw:flex-1 tw:bg-background tw:text-foreground"
              >
                <SelectValue placeholder={aorText('All AORs')}>
                  {selectedAorId
                    ? (aorOptions.find((opt) => opt.value === selectedAorId)
                        ?.label ?? aorText('All AORs'))
                    : aorText('All AORs')}
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="dark">
                <SelectItem value={ALL_VALUE}>{aorText('All AORs')}</SelectItem>
                {aorOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-2">
      <StylishNewSearchBar
        value={searchText}
        onChangeSearchTerm={onSearchChange}
        placeholder="Search"
      />
      <Row className="mt-2">
        <Col className="d-flex">
          <div className="d-flex align-items-center w-100 gap-2">
            <span>Incident:</span>
            <div className="flex-grow-1">
              <StylishNewSelect
                options={incidentOptions}
                value={incidentOptions.find(
                  (opt) => opt.value === selectedIncidentId
                )}
                isClearable={!incidentLocked}
                isDisabled={incidentLocked}
                onChange={(opt: { value: UUID; label: string } | null) => {
                  if (incidentLocked) return;
                  onIncidentChange(opt?.value ?? null);
                }}
              />
            </div>
          </div>
        </Col>
        <Col className="d-flex">
          <div className="d-flex align-items-center w-100 gap-2">
            <span>AOR:</span>
            <div className="flex-grow-1">
              <StylishNewSelect
                options={aorOptions}
                value={aorOptions.find((opt) => opt.value === selectedAorId)}
                isClearable
                onChange={(opt: { value: UUID; label: string } | null) =>
                  onAorChange(opt?.value ?? null)
                }
              />
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default NotificationsFilterBar;
